import type { Control, FieldValues } from 'react-hook-form';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import type { ContentI18n, SurveyExpiration, SurveyResponseType } from '@/shared/types/nps';
import { EXPIRATION_OPTIONS, SURVEY_RESPONSE_TYPE_LABEL } from '../../constants/survey';
import type { ContentI18nUpdater } from '../../model/content-i18n';
import { ContentI18nFields } from './content-i18n-fields';

interface IdentifiedFields {
  response_type: SurveyResponseType;
  identification_required: boolean;
  answer_bonus: boolean;
}

interface Props<T extends FieldValues & IdentifiedFields> {
  control?: Control<T>;
  /** Modo controlado sem react-hook-form */
  values?: IdentifiedFields & { response_expiration_time?: SurveyExpiration };
  onChange?: (patch: Partial<IdentifiedFields & { response_expiration_time?: SurveyExpiration }>) => void;
  disabled?: boolean;
  showExpiration?: boolean;
  /** Título + descrição + tipo (default true). */
  showBasics?: boolean;
  /** Toggles de identificação/bônus (default true quando IDENTIFIED). */
  showIdentifiedRules?: boolean;
  titleValue?: string;
  descriptionValue?: string;
  i18nValue?: ContentI18n | null;
  onTitleChange?: (v: string) => void;
  onDescriptionChange?: (v: string) => void;
  onI18nChange?: (update: ContentI18nUpdater) => void;
  titleError?: string;
  descriptionError?: string;
}

export function SurveySettingsFields({
  values,
  onChange,
  disabled,
  showExpiration = true,
  showBasics = true,
  showIdentifiedRules = true,
  titleValue = '',
  descriptionValue = '',
  i18nValue,
  onTitleChange,
  onDescriptionChange,
  onI18nChange,
  titleError,
  descriptionError,
}: Props<FieldValues & IdentifiedFields>) {
  const responseType = values?.response_type ?? 'ANONYMOUS';
  const expiration = values?.response_expiration_time ?? 'THREE_MONTHS';

  return (
    <div className="space-y-4">
      {showBasics ? (
        <>
          <ContentI18nFields
            disabled={disabled}
            titleValue={titleValue}
            descriptionValue={descriptionValue}
            i18nValue={i18nValue}
            onTitleChange={(v) => onTitleChange?.(v)}
            onDescriptionChange={(v) => onDescriptionChange?.(v)}
            onI18nChange={(v) => onI18nChange?.(v)}
            titleError={titleError}
            descriptionError={descriptionError}
            titlePlaceholder="Ex.: NPS pós-compra"
            descriptionPlaceholder="Descreva o objetivo da pesquisa"
          />
          <div className="space-y-2">
            <Label>Tipo de resposta</Label>
            <Select
              disabled={disabled}
              value={responseType}
              onValueChange={(v) => {
                const type = v as SurveyResponseType;
                onChange?.({
                  response_type: type,
                  ...(type === 'ANONYMOUS'
                    ? {
                        identification_required: false,
                        answer_bonus: false,
                        response_expiration_time: undefined,
                      }
                    : { response_expiration_time: values?.response_expiration_time ?? 'THREE_MONTHS' }),
                });
              }}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.entries(SURVEY_RESPONSE_TYPE_LABEL) as [SurveyResponseType, string][]).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      ) : null}

      {showExpiration && responseType === 'IDENTIFIED' ? (
        <div className="space-y-2">
          <Label>Expiração do link</Label>
          <Select
            disabled={disabled}
            value={expiration}
            onValueChange={(v) => onChange?.({ response_expiration_time: v as SurveyExpiration })}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {EXPIRATION_OPTIONS.map(([k, label]) => (
                <SelectItem key={k} value={k}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {showIdentifiedRules && responseType === 'IDENTIFIED' ? (
        <>
          <ToggleRow
            disabled={disabled}
            label="Exigir identificação (CPF)"
            hint="O respondente precisa informar o CPF."
            checked={values?.identification_required ?? false}
            onCheckedChange={(v) => onChange?.({ identification_required: v })}
          />
          <ToggleRow
            disabled={disabled}
            label="Pesquisa bonificada"
            hint="Concede bonificação ao respondente após enviar a pesquisa."
            checked={values?.answer_bonus ?? false}
            onCheckedChange={(v) => onChange?.({ answer_bonus: v })}
          />
        </>
      ) : null}
    </div>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  onCheckedChange,
  disabled,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-border bg-[hsl(var(--glass-bg)/0.4)] p-4">
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <Switch disabled={disabled} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}
